import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { toast } from '@hivebrite/design-system/Toast';

import Layout from 'hb-react/frontoffice/apps/Mentoring/components/Layout';
import { CreateMentor } from 'hb-react/frontoffice/apps/Mentoring/container/Application/Mentor/CreateMentor';
import { ReapplyMentor } from 'hb-react/frontoffice/apps/Mentoring/container/Application/Mentor/ReapplyMentor';
import { NotAuthorized } from 'hb-react/frontoffice/apps/Mentoring/container/Application/Shared/NotAuthorized';
import type {
  CustomAttributeType,
  MentorProfile,
  Program,
} from 'hb-react/frontoffice/apps/Mentoring/types';
import { getLowerCaseMentorLabel } from 'hb-react/frontoffice/apps/Mentoring/utils';

interface ApplyMentorFormsProps {
  program: Program;
  mentorProfile?: MentorProfile | { status: null };
  attributes: CustomAttributeType[];
}

export const ApplyMentorForms = ({
  program,
  mentorProfile,
  attributes,
}: ApplyMentorFormsProps) => {
  const {
    id,
    applied_to_be_mentor_text: applicationDescription,
    mentor_label: mentorLabel,
    name,
    mode,
  } = program;
  const navigate = useNavigate();

  const title = s__('Mentoring|Apply to become a %{label} in %{name}').i__({
    label: getLowerCaseMentorLabel(mentorLabel),
    name,
  });

  const onSuccess = () => {
    navigate(mode === 'admin_match' ? '/my-mentoring' : '../../mentors');
  };

  useEffect(() => {
    if (!mentorProfile?.status) {
      return;
    }

    if (['pending', 'accepted', 'on_pause'].includes(mentorProfile.status)) {
      toast(
        s__(
          'Mentoring|You have already applied to be a %{mentorLabel} of this %{programName}.',
        ).i__({
          mentorLabel: getLowerCaseMentorLabel(mentorLabel),
          programName: name,
        }),
      );

      navigate(`/my-mentoring/${id}/card/mentor`);
    }
  }, [mentorProfile?.status, mentorLabel, name, id, navigate]);

  if (mentorProfile?.status === 'rejected_can_reapply') {
    return (
      <Layout dataTestId="can-reapply-form">
        <ReapplyMentor
          program={program}
          attributes={attributes}
          applicationDescription={applicationDescription}
          mentorProfile={
            mentorProfile as MentorProfile & {
              status: 'rejected_can_reapply';
            }
          }
          title={title}
          onSuccess={onSuccess}
        />
      </Layout>
    );
  }

  if (mentorProfile?.status === 'rejected_cannot_reapply') {
    return (
      <NotAuthorized
        title={s__('Mentoring|Your application has been reviewed and rejected')}
        description={s__('Mentoring|You cannot reapply to this program.')}
      />
    );
  }

  if (mentorProfile?.status === 'terminated') {
    return (
      <NotAuthorized
        title={s__(
          'Mentoring|Your %{mentorLabel} profile has already been terminated',
        ).i__({
          mentorLabel: getLowerCaseMentorLabel(mentorLabel),
        })}
        description={s__('Mentoring|You cannot reapply to this program.')}
      />
    );
  }

  if (!mentorProfile?.status)
    return (
      <Layout dataTestId="create-form">
        <CreateMentor
          program={program}
          attributes={attributes}
          applicationDescription={applicationDescription}
          title={title}
          onSuccess={onSuccess}
        />
      </Layout>
    );

  return null;
};
